/**
 * The app's sounds — the realtime chime, and the success / error cues.
 *
 * One `HTMLAudioElement` per clip for the life of the tab. Two callers:
 *
 * - `presentToast()` in `realtime.service.ts` plays the CHIME, and is the sole
 *   path for all three realtime toasts. Riding the toast is what makes that
 *   sound inherit every suppression rule for free: a message you sent yourself,
 *   a message for the thread already on screen, and a drained catch-up burst all
 *   skip the toast, so they are silent without this module knowing anything
 *   about them.
 * - `useNotify()` in `composables/useToast.ts` plays SUCCESS / ERROR beside the
 *   toast it is already showing, so an outcome the user caused is heard as well
 *   as seen. Never automatic: `useToast()` stays silent, and only the `useNotify()`
 *   preset (or an explicit `{ sound: true }`) makes noise.
 *
 * AUTOPLAY POLICY. Browsers refuse `play()` until the page has seen a real user
 * gesture, and a WebSocket frame is emphatically not one. `primeNotificationSound()`
 * therefore arms a one-shot unlock on the first pointer/key/touch event — for
 * EVERY clip, because iOS grants the exemption per element, not per document,
 * and a clip first played after the gesture has passed would stay silent there.
 * The unlock is `play()` on a MUTED element, paused in the SAME synchronous
 * tick: WebKit lifts the element's gesture restriction inside `play()` itself,
 * so playback never has to begin for the unlock to take — and it never does,
 * because the pause lands before the media pipeline starts. It is inaudible by
 * construction on every engine. NOTHING outside `playAppSound()` ever makes a
 * sound; the unlock is the one other `play()` in this file and it is silent.
 *
 * Why `muted`, never `volume`: iOS ignores writes to `HTMLMediaElement.volume`
 * (it always reads 1 — the level belongs to the hardware buttons) but honours
 * `muted`. This unlock once played each clip at `volume = 0` and paused when
 * the play promise resolved; on an iPhone that was all three clips, audible,
 * on the first tap after launch — the app "playing the alert sound at start".
 * The volume is also never touched because a DEFERRED restore could land on a
 * real play issued a moment later and silence it. Until the gesture happens
 * `play()` rejects and the sound is simply absent — never an error, never an
 * exception.
 *
 * NOTHING HERE MAY THROW. It runs inside a realtime frame handler; an exception
 * would take the burst (and therefore the badge and the toast) down with it.
 * Every public function is total: it returns on any missing capability.
 *
 * Import-safe with no `Audio` and no `document` (happy-dom, SSR, the service
 * worker's bundle), so unrelated specs need no mock.
 */
import {
  APP_SOUND_SRC,
  NOTIFICATION_SOUND_MIN_INTERVAL_MS,
  NOTIFICATION_SOUND_VOLUME,
} from '@/config/notifications';
import type { AppSoundKind } from '@/config/notifications';

export type { AppSoundKind };

/** The gestures that count as "the user has interacted with this page". */
const UNLOCK_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;

/**
 * Creation order, and therefore the order the unlock plays them in. `notification`
 * leads because it is the one clip that can be needed without any further user
 * action.
 */
const KINDS: readonly AppSoundKind[] = ['notification', 'success', 'error'];

const elements = new Map<AppSoundKind, HTMLAudioElement>();
const lastPlayedAt = new Map<AppSoundKind, number>();
let primed = false;
let unlocked = false;

/**
 * Lift the autoplay restriction on one element without a sound escaping.
 *
 * Everything here is synchronous and lands inside the gesture that called it:
 * mute → `play()` → `pause()` → rewind → unmute. The restriction is lifted by
 * the `play()` call itself (WebKit removes it before playback begins), the pause
 * keeps the media pipeline from ever starting, and `muted` covers the engine
 * that ignores `volume` (iOS). Because nothing is deferred, a real
 * `playAppSound()` issued right after the gesture finds the element unmuted and
 * can never be paused or silenced by a late continuation.
 *
 * The play promise is REJECTED by the pause — an `AbortError` on every engine
 * that returns one. Expected, and swallowed so it cannot surface as an
 * unhandled rejection on the first tap of every session.
 */
function unlockElement(audio: HTMLAudioElement): void {
  try {
    audio.muted = true;
    const started = audio.play();
    if (started && typeof started.catch === 'function') {
      void started.catch(() => undefined);
    }
    audio.pause();
    audio.currentTime = 0;
  } catch {
    /* an engine that refuses even a muted play leaves this clip locked, not broken */
  } finally {
    try {
      audio.muted = false;
    } catch {
      /* nothing left to do */
    }
  }
}

/**
 * One handler shared by all three unlock events.
 *
 * Deliberately NOT three `{ once: true }` listeners: `once` removes only the
 * listener that fired, so a tap would leave the `keydown` and `touchstart`
 * handlers attached for the life of the tab.
 */
function onFirstGesture(): void {
  detachUnlockListeners();
  if (unlocked) return;
  unlocked = true;
  for (const audio of elements.values()) unlockElement(audio);
}

function detachUnlockListeners(): void {
  if (typeof document === 'undefined') return;
  for (const name of UNLOCK_EVENTS) {
    try {
      document.removeEventListener(name, onFirstGesture, true);
    } catch {
      /* already gone */
    }
  }
}

/**
 * Create the elements and arm the gesture unlock. Idempotent and cheap — called
 * from `notificationsDevice.prepare()` at boot, which itself runs from several
 * entry points.
 *
 * All three clips are built here rather than on first use so the unlock above
 * has something to unlock, and so the bytes (already precached, ~31 KB total)
 * are decoded before the moment they are needed.
 */
export function primeNotificationSound(): void {
  if (primed) return;
  if (typeof document === 'undefined' || typeof Audio === 'undefined') return;
  primed = true;
  for (const kind of KINDS) {
    try {
      const audio = new Audio(APP_SOUND_SRC[kind]);
      audio.preload = 'auto';
      audio.volume = NOTIFICATION_SOUND_VOLUME;
      elements.set(kind, audio);
    } catch {
      /* one clip the environment refuses to build is one silent cue, not a failure */
    }
  }
  if (elements.size === 0) return;
  for (const name of UNLOCK_EVENTS) {
    try {
      document.addEventListener(name, onFirstGesture, true);
    } catch {
      /* a listener we cannot attach simply means no unlock from that gesture */
    }
  }
}

/**
 * Play one clip, if this is a moment where a sound makes sense.
 *
 * The visibility guard is the exact complement of `notificationsDevice.show()`,
 * which only alerts while the document is HIDDEN: together they guarantee one
 * sound per event. Without it a backgrounded tab would sound here AND get the
 * operating system's own notification sound. It holds for the outcome cues too —
 * a hidden tab has no business making noise, and a hidden tab reaching an
 * outcome cue means the user navigated away mid-save.
 *
 * The throttle is per clip: a chime that just played must not swallow the
 * success sound of a save the user makes a moment later.
 */
export function playAppSound(kind: AppSoundKind): void {
  if (typeof document === 'undefined') return;
  if (document.visibilityState !== 'visible') return;
  const element = elements.get(kind);
  if (!element) return;

  const now = Date.now();
  if (now - (lastPlayedAt.get(kind) ?? 0) < NOTIFICATION_SOUND_MIN_INTERVAL_MS) return;
  lastPlayedAt.set(kind, now);

  try {
    // Rewind so a second event restarts the sound instead of no-oping on an
    // element that is already at its end.
    element.currentTime = 0;
    const started = element.play();
    // A play refused by the autoplay policy REJECTS. An uncaught rejection here
    // would log on every notification, so the catch is load-bearing.
    if (started && typeof started.catch === 'function') {
      void started.catch(() => undefined);
    }
  } catch {
    /* a sound is never worth an exception */
  }
}

/** The arrival chime. Named for its one caller, `presentToast()`. */
export function playNotificationChime(): void {
  playAppSound('notification');
}

/** Test seam: drop the elements and the listeners so a spec can start clean. */
export function resetNotificationSoundForTests(): void {
  detachUnlockListeners();
  elements.clear();
  lastPlayedAt.clear();
  primed = false;
  unlocked = false;
}
